import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SubjectIcon } from "@/components/SubjectIcon";
import { remap } from "@/lib/palette";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ArrowLeft, ListChecks, Sparkle, Robot, BookOpen, Lightbulb, FilePdf, CloudArrowDown, Books, Stop, MagicWand, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_LABEL = { cours: "Cours", methode: "Méthode", synthese: "Synthèse" };

export default function SubjectDetail() {
  const { examId, subjectName } = useParams();
  const navigate = useNavigate();
  const decoded = decodeURIComponent(subjectName);
  const [data, setData] = useState(null);
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState("moyen");
  const [genLoading, setGenLoading] = useState(false);
  const [exercise, setExercise] = useState("");
  const [offlineSaved, setOfflineSaved] = useState(false);
  const [chapter, setChapter] = useState("");
  const [courseLoading, setCourseLoading] = useState(false);
  const [fillingChapter, setFillingChapter] = useState(null);
  const [manual, setManual] = useState({ running: false, done: 0, total: 0 });
  const manualCancel = useRef(false);

  const reloadSubject = () =>
    api.get(`/exams/${examId}/subjects/${encodeURIComponent(decoded)}`).then((r) => setData(r.data));

  const generateChapter = async (ch) => {
    setFillingChapter(ch);
    try {
      await api.post("/courses/generate-chapter", { exam_id: examId, subject: decoded, chapter: ch });
    } catch (e) {
      if (e.response?.status === 429) {
        toast.warning("Limite IA atteinte — réessayez dans quelques minutes.");
        setFillingChapter(null);
        return;
      }
      // le proxy peut couper une génération longue : on vérifie via rechargement
    }
    const r = await api.get(`/exams/${examId}/subjects/${encodeURIComponent(decoded)}`);
    setData(r.data);
    const ok = (r.data.lessons || []).some((l) => l.chapter === ch && l.type === "cours" && l.ai_filled);
    if (ok) toast.success(`Cours complet généré : ${ch}`);
    else toast.error("Génération impossible pour ce chapitre (Prof IA indisponible).");
    setFillingChapter(null);
  };

  const runManual = async () => {
    const chapters = [...new Set((data?.lessons || [])
      .filter((l) => l.type === "cours" && l.chapter && !l.ai_filled)
      .map((l) => l.chapter))];
    if (!chapters.length) { toast.info("Le manuel est déjà complet ! 🎉"); return; }
    manualCancel.current = false;
    setManual({ running: true, done: 0, total: chapters.length });
    let done = 0;
    for (const ch of chapters) {
      if (manualCancel.current) break;
      try {
        await api.post("/courses/generate-chapter", { exam_id: examId, subject: decoded, chapter: ch });
      } catch (e) {
        if (e.response?.status === 429) {
          toast.warning("Limite IA atteinte — la génération reprendra plus tard.");
          break;
        }
        // sinon : le backend a pu terminer malgré un timeout proxy, on continue
      }
      done += 1;
      setManual({ running: true, done, total: chapters.length });
    }
    await reloadSubject();
    setManual({ running: false, done, total: chapters.length });
    if (!manualCancel.current && done > 0) toast.success(`Manuel enrichi : ${done} chapitre(s) traité(s) ✓`);
  };
  const stopManual = () => { manualCancel.current = true; };

  const generateCourse = async () => {
    if (!chapter.trim()) { toast.error("Indiquez un chapitre."); return; }
    setCourseLoading(true);
    try {
      await api.post("/courses/generate", { exam_id: examId, subject: decoded, chapter });
      await reloadSubject();
      setChapter("");
      toast.success("Cours complet généré et ajouté à la bibliothèque ! (+10 XP)");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Génération impossible (Prof IA indisponible).");
    } finally { setCourseLoading(false); }
  };

  const offlineKey = `yaya:offline:${examId}:${decoded}`;

  useEffect(() => {
    setOfflineSaved(!!localStorage.getItem(offlineKey));
    api.get(`/exams/${examId}/subjects/${encodeURIComponent(decoded)}`)
      .then((r) => setData(r.data))
      .catch(() => {
        const cached = localStorage.getItem(offlineKey);
        if (cached) { setData(JSON.parse(cached)); toast.info("Mode hors-ligne : contenu enregistré affiché."); }
        else navigate("/app/cours");
      });
  }, [examId, decoded, navigate, offlineKey]);

  const saveOffline = () => {
    if (!data) return;
    localStorage.setItem(offlineKey, JSON.stringify(data));
    setOfflineSaved(true);
    toast.success("Cours enregistré pour consultation hors-ligne.");
  };

  const downloadSubjectPdf = async () => {
    try {
      const res = await fetch(`${API}/subjects/pdf`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exam_id: examId, subject: decoded }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${decoded}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement PDF indisponible.");
    }
  };

  const downloadPdf = async (index, title) => {
    try {
      const res = await fetch(`${API}/lessons/pdf`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exam_id: examId, subject: decoded, index }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${title}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement PDF indisponible.");
    }
  };

  const generate = async () => {
    if (!topic.trim()) { toast.error("Indiquez un thème."); return; }
    setGenLoading(true); setExercise("");
    try {
      const { data: res } = await api.post("/tutor/generate-exercise", { subject: decoded, topic, difficulty });
      setExercise(res.content);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Génération impossible (Prof IA indisponible).");
    } finally {
      setGenLoading(false);
    }
  };

  if (!data) return <div className="h-64 animate-pulse rounded-2xl bg-sand" />;
  const { subject, lessons, quiz } = data;

  return (
    <div className="space-y-8">
      <Link to={`/app/cours/${examId}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-terracotta"><ArrowLeft /> {data.exam.name}</Link>

      <div className="flex items-center gap-4 animate-fade-up">
        <div className="grid h-14 w-14 place-items-center rounded-2xl" style={{ background: `${remap(subject.color)}1a`, color: remap(subject.color) }}>
          <SubjectIcon name={subject.icon} size={30} />
        </div>
        <div>
          <h1 className="font-display text-3xl font-extrabold">{subject.name}</h1>
          <p className="text-muted-foreground">{data.exam.name} · {data.exam.classe}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button data-testid="subject-pdf-btn" onClick={downloadSubjectPdf} variant="outline" className="rounded-full">
          <FilePdf weight="fill" className="mr-1.5 text-terracotta" /> Tout le cours en PDF
        </Button>
        <Button data-testid="offline-save-btn" onClick={saveOffline} variant="outline" className="rounded-full">
          <CloudArrowDown weight="fill" className={`mr-1.5 ${offlineSaved ? "text-forest" : "text-terracotta"}`} />
          {offlineSaved ? "Enregistré hors-ligne ✓" : "Enregistrer hors-ligne"}
        </Button>
        {manual.running ? (
          <Button data-testid="manual-stop-btn" onClick={stopManual} variant="outline" className="rounded-full border-destructive text-destructive">
            <Stop weight="fill" className="mr-1.5" /> Arrêter ({manual.done}/{manual.total})
          </Button>
        ) : (
          <Button data-testid="manual-generate-btn" onClick={runManual} className="rounded-full bg-forest text-white hover:bg-forest/90">
            <Books weight="fill" className="mr-1.5 text-gold" /> Compléter le manuel (IA)
          </Button>
        )}
      </div>

      {manual.running && (
        <div className="rounded-2xl border border-forest/30 bg-forest/5 p-4" data-testid="manual-progress">
          <div className="flex items-center justify-between text-sm font-semibold text-forest">
            <span className="flex items-center gap-2"><MagicWand weight="fill" /> Génération du manuel en cours…</span>
            <span>{manual.done}/{manual.total} chapitres</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-sand">
            <div className="h-full rounded-full bg-forest transition-all" style={{ width: `${manual.total ? (manual.done / manual.total) * 100 : 0}%` }} />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Vous pouvez continuer à réviser : la génération se poursuit en arrière-plan.</p>
        </div>
      )}

      {/* Quiz CTA */}
      {quiz && (
        <div className="flex flex-col items-start justify-between gap-3 rounded-2xl border border-terracotta/30 bg-terracotta/5 p-5 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-terracotta/15 text-terracotta"><ListChecks weight="fill" size={22} /></div>
            <div>
              <div className="font-semibold">{quiz.title}</div>
              <div className="text-sm text-muted-foreground">{quiz.count} questions · niveau {quiz.difficulty} · gagnez des XP</div>
            </div>
          </div>
          <Button data-testid="start-quiz-btn" onClick={() => navigate(`/app/quiz/${encodeURIComponent(quiz.id)}`)} className="rounded-full bg-terracotta text-white hover:bg-[#E03E00]">Démarrer le quiz</Button>
        </div>
      )}

      {/* Lessons */}
      <div>
        <h2 className="mb-4 font-display text-xl font-bold">Leçons</h2>
        {lessons.length ? (
          <Accordion type="single" collapsible className="space-y-3">
            {lessons.map((l, idx) => (
              <AccordionItem key={l.id} value={l.id} className="overflow-hidden rounded-2xl border border-border bg-white px-5">
                <AccordionTrigger data-testid={`lesson-${l.id}`} className="hover:no-underline">
                  <div className="flex items-center gap-3 text-left">
                    <BookOpen className="text-terracotta" size={20} />
                    <div>
                      <div className="font-semibold">{l.title}</div>
                      <div className="text-xs text-muted-foreground">{TYPE_LABEL[l.type] || l.type} · {l.duration} min</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pb-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <button data-testid={`pdf-${l.id}`} onClick={() => downloadPdf(idx, l.title)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-forest transition hover:bg-sand">
                        <FilePdf weight="fill" size={15} className="text-terracotta" /> Télécharger en PDF
                      </button>
                      {l.chapter && l.type === "cours" && (
                        l.ai_filled ? (
                          <span data-testid={`chapter-filled-${l.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-forest/10 px-3 py-1.5 text-xs font-semibold text-forest">
                            <CheckCircle weight="fill" size={15} /> Cours complet généré par l'IA
                          </span>
                        ) : (
                          <button data-testid={`chapter-generate-${l.id}`} onClick={() => generateChapter(l.chapter)} disabled={fillingChapter === l.chapter}
                            className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#E03E00] disabled:opacity-60">
                            <MagicWand weight="fill" size={15} /> {fillingChapter === l.chapter ? "Rédaction du cours…" : "Générer le cours complet"}
                          </button>
                        )
                      )}
                    </div>
                    {l.summary && (
                      <div className="rounded-xl bg-terracotta/5 p-4 text-sm">
                        <div className="mb-1 text-xs font-bold uppercase tracking-wide text-terracotta">En bref</div>
                        {l.summary}
                      </div>
                    )}
                    {l.objectives?.length > 0 && (
                      <div>
                        <div className="mb-1 text-xs font-bold uppercase tracking-wide text-forest">Objectifs</div>
                        <ul className="list-disc space-y-1 pl-5 text-sm">{l.objectives.map((o, k) => <li key={k}>{o}</li>)}</ul>
                      </div>
                    )}
                    {l.prerequisites && (
                      <div className="text-sm"><span className="font-bold text-forest">Prérequis : </span><span className="text-ink/70">{l.prerequisites}</span></div>
                    )}
                    <div className="prose-yaya text-sm"><ReactMarkdown remarkPlugins={[remarkGfm]}>{l.content}</ReactMarkdown></div>
                    {l.common_errors?.length > 0 && (
                      <div className="rounded-xl bg-destructive/5 p-4">
                        <div className="mb-1 text-xs font-bold uppercase tracking-wide text-destructive">Erreurs fréquentes</div>
                        <ul className="list-disc space-y-1 pl-5 text-sm text-ink/80">{l.common_errors.map((o, k) => <li key={k}>{o}</li>)}</ul>
                      </div>
                    )}
                    {l.tips?.length > 0 && (
                      <div className="rounded-xl bg-gold/10 p-4">
                        <div className="mb-1 text-xs font-bold uppercase tracking-wide text-[#9a6b00]">Conseils de réussite</div>
                        <ul className="list-disc space-y-1 pl-5 text-sm text-ink/80">{l.tips.map((o, k) => <li key={k}>{o}</li>)}</ul>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-white p-8 text-center">
            <Sparkle weight="fill" className="mx-auto text-terracotta" size={28} />
            <p className="mt-2 text-sm text-muted-foreground">Aucun cours pré-enregistré ici pour l'instant — utilisez le Professeur IA ci-dessous pour générer cours et exercices à la demande.</p>
          </div>
        )}
      </div>

      {/* AI full-course generator (Academic AI Engine) */}
      <div className="rounded-2xl border border-terracotta/30 bg-terracotta/5 p-6">
        <div className="flex items-center gap-2"><Sparkle weight="fill" className="text-terracotta" size={22} /><h2 className="font-display text-xl font-bold">Générer un cours complet avec l'IA</h2></div>
        <p className="mt-1 text-sm text-muted-foreground">Indiquez un chapitre : le Professeur IA rédige un cours structuré complet (objectifs, notions, exemples, méthode, exercices) et l'ajoute à la bibliothèque.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input data-testid="gen-course-chapter" value={chapter} onChange={(e) => setChapter(e.target.value)} placeholder={`Chapitre en ${decoded}…`}
            className="flex-1 rounded-xl border border-border bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-terracotta" />
          <Button data-testid="gen-course-submit" onClick={generateCourse} disabled={courseLoading} className="rounded-xl bg-terracotta text-white hover:bg-[#E03E00]">
            {courseLoading ? "Rédaction du cours…" : "Générer le cours"}
          </Button>
        </div>
      </div>

      {/* AI exercise generator */}
      <div className="rounded-2xl border border-border bg-white p-6">
        <div className="flex items-center gap-2"><Lightbulb weight="fill" className="text-gold" size={22} /><h2 className="font-display text-xl font-bold">Générer un exercice avec l'IA</h2></div>
        <p className="mt-1 text-sm text-muted-foreground">Le Professeur IA crée un exercice adapté avec corrigé détaillé.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input data-testid="gen-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={`Thème en ${decoded}…`}
            className="flex-1 rounded-xl border border-border bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-terracotta" />
          <select data-testid="gen-difficulty" value={difficulty} onChange={(e) => setDifficulty(e.target.value)}
            className="rounded-xl border border-border bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-terracotta">
            <option value="facile">Facile</option><option value="moyen">Moyen</option><option value="difficile">Difficile</option>
          </select>
          <Button data-testid="gen-submit" onClick={generate} disabled={genLoading} className="rounded-xl bg-forest text-white hover:bg-forest/90">
            {genLoading ? "Génération…" : "Générer"}
          </Button>
        </div>
        {exercise && (
          <div className="prose-yaya mt-5 rounded-xl bg-cream p-5 text-sm"><ReactMarkdown remarkPlugins={[remarkGfm]}>{exercise}</ReactMarkdown></div>
        )}
        <Link to="/app/tuteur" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-terracotta hover:underline">
          <Robot weight="fill" /> Poser une question au Professeur IA
        </Link>
      </div>
    </div>
  );
}
